import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useTranslation } from '@kgc/i18n';
import type { IPhotoStorageAdapter, OneTimeGrantSubmitPayload } from '@kgc/ui';
import { OneTimeGrantDialog } from '@kgc/ui';
import { FileText, Users } from 'lucide-react';
import * as React from 'react';
import type { PermanentGrantSubmitPayload } from './PermanentGrantDialog';
import { PermanentGrantDialog } from './PermanentGrantDialog';

/**
 * GrantCreateModalRouter — STORY-1212 (B-13) "+ Új grant" → 2 primary button
 * → respective dialog flow (Q3 gate-7 decision 2026-04-24: 2 külön dialog).
 *
 * Router flow:
 *   parent `open` → chooser dialog with 2 buttons
 *   click PERMANENT → close chooser + open PermanentGrantDialog
 *   click ONE_TIME  → close chooser + open OneTimeGrantDialog (shared @kgc/ui
 *                     component from B-12)
 *
 * Why 2 separate dialogs (Q3 decision):
 *  - PERMANENT and ONE_TIME share very little form shape (granteePartnerId
 *    vs granteeData + documentIds + skipWith-reason) — single dialog would
 *    require aggressive conditional rendering.
 *  - Each dialog has independent submit shape; routing at the router level
 *    keeps each dialog form focused.
 *
 * Submit handlers are independent; parent provides both (or omits one to
 * hide the corresponding button).
 */

export type GrantCreateChoice = 'permanent' | 'oneTime';

export interface GrantCreateModalRouterProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Submit handler for PERMANENT path. Omit to hide the "PERMANENT" primary button. */
  onSubmitPermanent?: (payload: PermanentGrantSubmitPayload) => Promise<void> | void;
  /** Submit handler for ONE_TIME path. Omit to hide the "ONE_TIME" primary button. */
  onSubmitOneTime?: (payload: OneTimeGrantSubmitPayload) => Promise<void> | void;
  /** Photo storage adapter for ONE_TIME dialog's PhotoUpload. Required if ONE_TIME path is enabled. */
  photoAdapter?: IPhotoStorageAdapter;
}

export function GrantCreateModalRouter({
  open,
  onOpenChange,
  onSubmitPermanent,
  onSubmitOneTime,
  photoAdapter,
}: GrantCreateModalRouterProps) {
  const { t } = useTranslation();
  const [activeDialog, setActiveDialog] = React.useState<GrantCreateChoice | null>(null);

  // B-13 Edge P2 #1 — if only ONE handler is enabled, skip the chooser and
  // auto-route to that dialog. Prevents a "chooser" with a single button +
  // empty space titled "Válaszd ki..." which is awkward UX.
  const permanentEnabled = onSubmitPermanent !== undefined;
  const oneTimeEnabled = onSubmitOneTime !== undefined && photoAdapter !== undefined;
  const showChooser = permanentEnabled && oneTimeEnabled;

  React.useEffect(() => {
    if (!open) {
      setActiveDialog(null);
      return;
    }
    // Auto-route single-handler case.
    if (!showChooser) {
      if (permanentEnabled) setActiveDialog('permanent');
      else if (oneTimeEnabled) setActiveDialog('oneTime');
    }
  }, [open, showChooser, permanentEnabled, oneTimeEnabled]);

  // Chooser open state = router open AND no specific dialog opened yet.
  const chooserOpen = open && showChooser && activeDialog === null;

  const handleChoose = (choice: GrantCreateChoice) => {
    setActiveDialog(choice);
  };

  const handleChooserOpenChange = (next: boolean) => {
    if (!next) {
      setActiveDialog(null);
      onOpenChange(false);
    }
  };

  const handleDialogClose = () => {
    setActiveDialog(null);
    onOpenChange(false);
  };

  // Wrap submit handlers so parent gets clean close after success.
  const handlePermanentSubmit = React.useCallback(
    async (payload: PermanentGrantSubmitPayload) => {
      if (!onSubmitPermanent) return;
      await onSubmitPermanent(payload);
      handleDialogClose();
    },
    [onSubmitPermanent]
  );

  const handleOneTimeSubmit = React.useCallback(
    async (payload: OneTimeGrantSubmitPayload) => {
      if (!onSubmitOneTime) return;
      await onSubmitOneTime(payload);
      handleDialogClose();
    },
    [onSubmitOneTime]
  );

  return (
    <>
      <Dialog open={chooserOpen} onOpenChange={handleChooserOpenChange}>
        <DialogContent className="max-w-md" data-testid="grant-create-chooser">
          <DialogHeader>
            <DialogTitle>{t('authGrant.chooser.title')}</DialogTitle>
          </DialogHeader>

          <p className="text-sm text-muted-foreground">{t('authGrant.chooser.subtitle')}</p>

          <div className="grid gap-2 py-2">
            {onSubmitPermanent && (
              <Button
                variant="outline"
                className="h-auto justify-start p-3 text-left"
                onClick={() => handleChoose('permanent')}
                data-testid="grant-create-choose-permanent"
              >
                <div className="flex items-start gap-3">
                  <Users className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                  <div>
                    <div className="font-medium">{t('authGrant.chooser.permanentLabel')}</div>
                    <div className="text-xs text-muted-foreground">
                      {t('authGrant.chooser.permanentDesc')}
                    </div>
                  </div>
                </div>
              </Button>
            )}

            {onSubmitOneTime && photoAdapter && (
              <Button
                variant="outline"
                className="h-auto justify-start p-3 text-left"
                onClick={() => handleChoose('oneTime')}
                data-testid="grant-create-choose-one-time"
              >
                <div className="flex items-start gap-3">
                  <FileText className="mt-0.5 h-5 w-5 shrink-0 text-sky-600 dark:text-sky-400" />
                  <div>
                    <div className="font-medium">{t('authGrant.chooser.oneTimeLabel')}</div>
                    <div className="text-xs text-muted-foreground">
                      {t('authGrant.chooser.oneTimeDesc')}
                    </div>
                  </div>
                </div>
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {onSubmitPermanent && (
        <PermanentGrantDialog
          open={activeDialog === 'permanent'}
          onOpenChange={next => {
            if (!next) handleDialogClose();
          }}
          onSubmit={handlePermanentSubmit}
        />
      )}

      {onSubmitOneTime && photoAdapter && (
        <OneTimeGrantDialog
          open={activeDialog === 'oneTime'}
          onOpenChange={next => {
            if (!next) handleDialogClose();
          }}
          onSubmit={handleOneTimeSubmit}
          photoAdapter={photoAdapter}
        />
      )}
    </>
  );
}
